import { useTranslation } from 'react-i18next';

interface Category {
  id: string;
  label: string;
  icon: string;
  count: number;
}

interface ProductFiltersProps {
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

export default function ProductFilters({
  selectedCategory,
  setSelectedCategory,
  searchQuery,
  setSearchQuery,
}: ProductFiltersProps) {
  const { t } = useTranslation('common');

  const categories: Category[] = [
    { id: 'all', label: t('products.allCategories'), icon: 'ri-apps-line', count: 22 },
    { id: 'engine', label: t('products.engineParts'), icon: 'ri-settings-3-line', count: 6 },
    { id: 'braking', label: t('products.brakingSystem'), icon: 'ri-stop-circle-line', count: 4 },
    { id: 'suspension', label: t('products.suspension'), icon: 'ri-car-line', count: 4 },
    { id: 'lighting', label: t('products.lighting'), icon: 'ri-flashlight-line', count: 4 },
    { id: 'trailer', label: t('products.trailerParts'), icon: 'ri-truck-line', count: 4 },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Search Bar */}
      <div className="relative max-w-sm">
        <div className="w-10 h-10 flex items-center justify-center absolute left-0 top-1/2 -translate-y-1/2 pointer-events-none">
          <i className="ri-search-line text-gray-400 text-base"></i>
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('products.searchPlaceholder')}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        )}
      </div>

      {/* Category Pills — horizontal scroll on mobile, wraps on desktop */}
      <div className="flex gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer flex-shrink-0 ${
              selectedCategory === cat.id
                ? 'bg-teal-600 text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:border-teal-400 hover:text-teal-600'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${cat.icon} text-sm`}></i>
            </div>
            <span>{cat.label}</span>
            <span
              className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                selectedCategory === cat.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
              }`}
            >
              {cat.count}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
